// CustomDropdown.js
import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    FlatList,
    TextInput,
    StyleSheet,
    Dimensions,
    Platform,
    Pressable
} from 'react-native';

const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
import Ionicons from 'react-native-vector-icons/Ionicons';

const CustomDropdown = ({
    data = [],
    selectedValue,
    onSelect,
    placeholder = 'নির্বাচন করুন',
    placeholderTextColor = '#989898',
    selectedTextColor = '#333333',
    containerStyle = {},
    dropdownButtonStyle = {},
    dropdownListStyle = {},
    itemStyle = {},
    itemTextStyle = {},
    searchPlaceholder = 'সার্চ করুন...',
    showSearch = null,
    maxHeight = 300,
    zIndex = 1000,
    selectOptionsWidth = 250,
    onOpenChange, // New prop
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const containerRef = useRef(null);
    const [dropdownWidth, setDropdownWidth] = useState(selectOptionsWidth);

    const shouldShowSearch = showSearch !== null
        ? showSearch
        : data.length > 10;

    useEffect(() => {
        if (searchText.trim()) {
            const filtered = data.filter(item =>
                item.label.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredData(filtered);
        } else {
            setFilteredData(data);
        }
    }, [searchText, data]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchText('');
                if (onOpenChange) {
                    onOpenChange(false);
                }
            }
        };

        if (Platform.OS === 'web') {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, onOpenChange]);

    useEffect(() => {
        if (containerRef.current && Platform.OS === 'web') {
            const measureWidth = () => {
                if (containerRef.current) {
                    const buttonWidth = containerRef.current.offsetWidth;
                    setDropdownWidth(Math.max(buttonWidth + 20, selectOptionsWidth));
                }
            };
            measureWidth();
            window.addEventListener('resize', measureWidth);
            return () => window.removeEventListener('resize', measureWidth);
        }
    }, [selectOptionsWidth]);

    const handleSelect = (item) => {
        onSelect(item.value);
        setIsOpen(false);
        setSearchText('');
        if (onOpenChange) {
            onOpenChange(false);
        }
    };

    const getSelectedLabel = () => {
        const selected = data.find(item => item.value === selectedValue);
        if (selected) {
            return selected.label;
        }
        return placeholder;
    };

    const toggleDropdown = () => {
        const newState = !isOpen;
        setIsOpen(newState);
        if (onOpenChange) {
            onOpenChange(newState);
        }
    };

    const renderItem = ({ item }) => (
        <Pressable
            onPress={() => handleSelect(item)}
            style={({ hovered }) => [
                styles.dropdownItem,
                itemStyle,
                selectedValue === item.value && styles.selectedItem,
                hovered && styles.hoverItem,
            ]}
        >
            {({ hovered }) => (
                <Text
                    style={[
                        styles.itemText,
                        itemTextStyle,
                        item.labelStyle,
                        selectedValue === item.value && styles.selectedItemText,
                        hovered && styles.hoverItemText,
                    ]}
                    numberOfLines={2}
                >
                    {item.label}
                </Text>
            )}
        </Pressable>
    );

    const renderEmptyList = () => (
        <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>কোনো ডাটা পাওয়া যায়নি</Text>
        </View>
    );

    const getDropdownListWidth = () => {
        if (Platform.OS === 'web') {
            return { minWidth: dropdownWidth };
        }
        return { minWidth: selectOptionsWidth };
    };

    return (
        <View
            ref={containerRef}
            style={[styles.container, containerStyle, { zIndex }]}
            onLayout={(event) => {
                if (Platform.OS !== 'web' && event.nativeEvent.layout) {
                    const buttonWidth = event.nativeEvent.layout.width;
                    setDropdownWidth(Math.max(buttonWidth + 20, selectOptionsWidth));
                }
            }}
        >
            <TouchableOpacity
                style={[styles.dropdownButton, dropdownButtonStyle]}
                onPress={toggleDropdown}
                activeOpacity={0.7}
            >
                <Text
                    style={[
                        styles.selectedText,
                        (!selectedValue || selectedValue === '') && { color: placeholderTextColor },
                        (selectedValue && selectedValue !== '') && { color: selectedTextColor },
                    ]}
                    numberOfLines={1}
                >
                    {getSelectedLabel()}
                </Text>
                <Ionicons
                    name={isOpen ? "chevron-up" : "chevron-down"}
                    size={18}
                    color="#555"
                />
            </TouchableOpacity>

            {isOpen && (
                <View style={[
                    styles.dropdownList,
                    dropdownListStyle,
                    {
                        maxHeight: maxHeight,
                        height: 'auto',
                        ...getDropdownListWidth(),
                    }
                ]}>
                    {shouldShowSearch && (
                        <View style={styles.searchContainer}>
                            <TextInput
                                style={styles.searchInput}
                                placeholder={searchPlaceholder}
                                placeholderTextColor="#999"
                                value={searchText}
                                onChangeText={setSearchText}
                                autoFocus={Platform.OS === 'ios' || Platform.OS === 'android'}
                            />
                            {searchText.length > 0 && (
                                <TouchableOpacity
                                    style={styles.clearButton}
                                    onPress={() => setSearchText('')}
                                >
                                    <Text style={styles.clearButtonText}>✕</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    )}

                    <FlatList
                        data={filteredData}
                        keyExtractor={(item, index) => `${item.value}-${index}`}
                        renderItem={renderItem}
                        ListEmptyComponent={renderEmptyList}
                        showsVerticalScrollIndicator={true}
                        keyboardShouldPersistTaps="handled"
                        nestedScrollEnabled={true}
                        style={styles.flatList}
                        contentContainerStyle={styles.flatListContent}
                        initialNumToRender={10}
                        maxToRenderPerBatch={20}
                        windowSize={5}
                    />
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        position: 'relative',
    },
    dropdownButton: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#000',
        borderRadius: 4,
        backgroundColor: '#FFF',
        paddingHorizontal: 10,
        paddingVertical: Platform.OS === 'web' ? 5 : 10,
        minHeight: 40,
    },
    selectedText: {
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        flex: 1,
    },
    dropdownList: {
        position: 'absolute',
        top: '100%',
        left: Platform.OS === 'web' ? '-10px' : -10,
        right: Platform.OS === 'web' ? '-10px' : -10,
        marginTop: 5,
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        overflow: 'hidden',
        elevation: 5,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        zIndex: 1001,
    },
    flatList: {
        flexGrow: 0,
    },
    flatListContent: {
        flexGrow: 1,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        // borderBottomWidth: 1,
        // borderBottomColor: '#e0e0e0',
        paddingHorizontal: 5,
        position: 'relative',
    },
    searchInput: {
        flex: 1,
        height: 40,
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#333',
        paddingRight: 30,
    },
    clearButton: {
        position: 'absolute',
        right: 10,
        padding: 5,
    },
    clearButtonText: {
        fontSize: 14,
        color: '#999',
    },
    dropdownItem: {
        paddingHorizontal: 15,
        paddingVertical: 5,
        // borderBottomWidth: 1,
        // borderBottomColor: '#acacac',
        // minHeight: 45,
        justifyContent: 'center',
    },
    selectedItem: {
        backgroundColor: '#f8f8f8',
    },
    itemText: {
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#333',
        lineHeight: 16,
        fontWeight: "inherit"
    },
    selectedItemText: {
        color: '#000',
        fontWeight: '500',
    },
    emptyContainer: {
        padding: 20,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 100,
    },
    emptyText: {
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#999',
        textAlign: 'center',
    },

    selectedItem: {
        // backgroundColor: '#007BFF',
    },

    selectedItemText: {
        // color: '#FFFFFF',
        // fontWeight: '500',
    },
    hoverItem: {
        backgroundColor: '#007BFF', // Blue
    },

    hoverItemText: {
        color: '#FFFFFF', // White
    },
});

export default CustomDropdown;